import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  ScrollView,
  Dimensions,
} from 'react-native';
import Svg, { Circle } from 'react-native-svg';
import DLOTooltip from './DLOTooltip';

// Legend dot icons
const LegendDot = ({ color }: { color: string }) => (
  <Svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <Circle cx="6" cy="6" r="6" fill={color} />
  </Svg>
);

// Data interfaces
export interface ChartDataPoint {
  day: string;
  estimatedHours: number;
  actualHours: number;
}

export interface LegendItem {
  label: string;
  color: string;
}

export interface DLOBarChartProps {
  title?: string;
  data: ChartDataPoint[];
  legend?: LegendItem[];
  maxValue?: number;
  style?: any;
}

const DLOBarChart: React.FC<DLOBarChartProps> = ({
  title = 'Performance by Day of Week',
  data,
  legend = [
    { label: 'Estimated Hours', color: '#33ABDA' },
    { label: 'Actual Hours', color: '#B05788' },
  ],
  maxValue,
  style,
}) => {
  // Get screen dimensions for responsive design
  const screenWidth = Dimensions.get('window').width;
  const screenHeight = Dimensions.get('window').height;
  
  // Responsive dimensions
  const isSmallScreen = screenWidth < 768;
  const isMediumScreen = screenWidth >= 768 && screenWidth < 1024;
  const isLargeScreen = screenWidth >= 1024;
  
  // Calculate responsive container dimensions
  const getResponsiveDimensions = () => {
    if (isSmallScreen) {
      return {
        containerWidth: Math.min(screenWidth - 32, 400),
        containerHeight: 300,
        chartAreaHeight: 160,
        barWidth: 16,
        fontSize: 12,
        titleFontSize: 14,
      };
    } else if (isMediumScreen) {
      return {
        containerWidth: Math.min(screenWidth - 64, 500),
        containerHeight: 320,
        chartAreaHeight: 180,
        barWidth: 20,
        fontSize: 12,
        titleFontSize: 15,
      };
    } else {
      return {
        containerWidth: Math.min(screenWidth - 96, 607),
        containerHeight: 354,
        chartAreaHeight: 200,
        barWidth: 24,
        fontSize: 12,
        titleFontSize: 16,
      };
    }
  };
  
  const responsiveDims = getResponsiveDimensions();
  const [layout, setLayout] = useState({ width: 0, height: 0 });

  // Handle empty data gracefully
  if (!data || data.length === 0) {
    return (
      <View style={[styles.containerFill, style]} onLayout={e => {
        const { width, height } = e.nativeEvent.layout;
        if (width !== layout.width || height !== layout.height) {
          setLayout({ width, height });
        }
      }}>
        <View style={[styles.container, { minWidth: 300 }] }>
          <View style={styles.header}>
            <Text style={[styles.title, { fontSize: responsiveDims.titleFontSize }]}>{title}</Text>
          </View>
          <View style={styles.emptyState}>
            <Text style={[styles.emptyStateText, { fontSize: responsiveDims.fontSize }]}>No data available</Text>
          </View>
        </View>
      </View>
    );
  }

  // Calculate dynamic maxValue if not provided
  const dynamicMaxValue = maxValue || Math.max(
    ...data.map(d => Math.max(d.estimatedHours, d.actualHours)),
    10 // Minimum value of 10 to ensure readable chart
  );
  
  // Round up to nearest 5 for better Y-axis labels
  const chartMaxValue = Math.ceil(dynamicMaxValue / 5) * 5;
  
  // Responsive dimensions
  const containerWidth = layout.width || responsiveDims.containerWidth;
  const containerHeight = layout.height || responsiveDims.containerHeight;
  const chartAreaLeft = isSmallScreen ? 32 : 40;
  const chartAreaTop = isSmallScreen ? 60 : 70;
  const chartAreaWidth = containerWidth - chartAreaLeft - 24;
  const computedChartAreaHeight = responsiveDims.chartAreaHeight; // fallback base
  const chartAreaHeight = layout.height
    ? Math.max(120, layout.height - 160) // leave space for header + legend
    : computedChartAreaHeight;
  const actualChartWidth = chartAreaWidth - 8;

  // Responsive bar calculations
  const barWidth = responsiveDims.barWidth;
  const barSpacing = isSmallScreen ? 6 : 8; // Space between estimated and actual bars
  const totalDays = data.length;
  
  const barGroupWidth = (barWidth * 2) + barSpacing;


  // Generate Y-axis labels with nice whole number steps
  function getNiceStep(min: number, max: number, maxTicks = 8) {
    const range = max - min;
    if (range <= 0) return 1;
    const roughStep = range / maxTicks;
    const pow10 = Math.pow(10, Math.floor(Math.log10(roughStep)));
    let step = pow10;
    if (roughStep / pow10 > 5) step = pow10 * 5;
    else if (roughStep / pow10 > 2) step = pow10 * 2;
    return Math.max(1, Math.round(step));
  }

  const yAxisStep = getNiceStep(0, chartMaxValue);
  const yAxisLabels = [];
  for (let i = 0; i <= chartMaxValue; i += yAxisStep) {
    yAxisLabels.push(i);
  }
  // Always include chartMaxValue as last label if not already present
  if (yAxisLabels.length === 0 || yAxisLabels[yAxisLabels.length - 1] < chartMaxValue) {
    yAxisLabels.push(chartMaxValue);
  }

  // Calculate bar heights based on chart proportions
  const getBarHeight = (value: number) => {
    return (value / chartMaxValue) * chartAreaHeight;
  };

  // Calculate X position for each day group based on dynamic spacing
  const getDayGroupX = (dayIndex: number) => {
    // Calculate the center position between grid lines
    const gridLineSpacing = actualChartWidth / totalDays;
    const centerX = (dayIndex + 0.5) * gridLineSpacing;
    // Adjust for bar group width to center the bars
    return centerX - (barGroupWidth / 2);
  };

  // Calculate grid line positions to create equal segments
  const getGridLineX = (lineIndex: number) => {
    return (lineIndex * actualChartWidth) / totalDays;
  };

  return (
    <View
      onLayout={e => {
        const { width, height } = e.nativeEvent.layout;
        if (width !== layout.width || height !== layout.height) {
          setLayout({ width, height });
        }
      }}
      style={[styles.containerFill, style]}
    >
      <View style={[styles.innerContainer, { width: containerWidth, height: containerHeight }]}> 
        {/* Header */}
        <View style={styles.header}>
          <Text style={[styles.title, { fontSize: responsiveDims.titleFontSize }]}>{title}</Text>
        </View>

        {/* Chart Container */}
        <View style={styles.chartWrapper}>
          {/* Y-Axis Labels */}
          <View style={[styles.yAxisContainer, { width: chartAreaLeft }]}>
            {yAxisLabels.reverse().map((value, index) => (
              <View key={value} style={[
                styles.yAxisLabelWrapper, 
                { 
                  top: chartAreaTop + (index * (chartAreaHeight / (yAxisLabels.length - 1))) - 9,
                  right: isSmallScreen ? 4 : 8,
                }
              ]}>
                <Text style={[styles.yAxisLabel, { fontSize: responsiveDims.fontSize }]}>{value}</Text>
              </View>
            ))}
          </View>

          {/* Chart Area with Grid */}
          <View style={[styles.chartArea, { 
            left: chartAreaLeft, 
            top: chartAreaTop, 
            width: chartAreaWidth, 
            height: chartAreaHeight 
          }]}>
            {/* Horizontal Grid Lines */}
            {yAxisLabels.map((value, index) => (
              <View
                key={`grid-${value}`}
                style={[
                  styles.gridLine,
                  {
                    top: index * (chartAreaHeight / (yAxisLabels.length - 1)),
                    width: actualChartWidth,
                  },
                ]}
              />
            ))}

            {/* Vertical Grid Lines - positioned to create equal segments */}
            {Array.from({ length: totalDays + 1 }, (_, index) => (
              <View
                key={`vgrid-${index}`}
                style={[
                  styles.verticalGridLine,
                  {
                    left: getGridLineX(index),
                    height: chartAreaHeight,
                  },
                ]}
              />
            ))}

            {/* Data Bars - structured like frameParent in reference */}
            <View style={[styles.barsContainer, { left: 0, top: 0 }]}>
              {data.map((dataPoint, index) => {
                const estimatedHeight = getBarHeight(dataPoint.estimatedHours);
                const actualHeight = getBarHeight(dataPoint.actualHours);
                const groupX = getDayGroupX(index);

                return (
                  <View key={dataPoint.day} style={[styles.barGroup, { left: groupX }]}>
                    {/* Estimated Hours Bar with individual tooltip */}
                    <DLOTooltip
                      content={`${dataPoint.estimatedHours.toFixed(1)}`}
                      position="top"
                      trigger="hover"
                      horizontalOffset={16}
                      verticalOffset={15}
                      minWidth={45}
                      maxWidth={80}
                      padding={8}
                      textAlign="center"
                      fontSize={12}
                    >
                      <View
                        style={[
                          styles.bar,
                          {
                            width: barWidth,
                            height: estimatedHeight,
                            backgroundColor: legend[0]?.color || '#33ABDA',
                          },
                        ]}
                      />
                    </DLOTooltip>
                    
                    {/* Actual Hours Bar with individual tooltip */}
                    <DLOTooltip
                      content={`${dataPoint.actualHours.toFixed(1)}`}
                      position="top"
                      trigger="hover"
                      horizontalOffset={16}
                      verticalOffset={15}
                      minWidth={45}
                      maxWidth={80}
                      padding={8}
                      textAlign="center"
                      fontSize={12}
                    >
                      <View
                        style={[
                          styles.bar,
                          {
                            width: barWidth,
                            height: dataPoint.actualHours > 0 ? actualHeight : 0,
                            backgroundColor: dataPoint.actualHours > 0 
                              ? (legend[1]?.color || '#B05788')
                              : 'rgba(176, 87, 136, 0)',
                          },
                        ]}
                      />
                    </DLOTooltip>
                  </View>
                );
              })}
            </View>
          </View>

          {/* X-Axis Labels - positioned at centers between grid lines */}
          <View style={[styles.xAxisContainer, {
            left: chartAreaLeft,
            top: chartAreaTop + chartAreaHeight + 4,
            width: actualChartWidth
          }]}>
            {data.map((dataPoint, index) => {
              // Calculate the center position between grid lines for each day
              const segmentWidth = actualChartWidth / totalDays;
              const centerX = (index * segmentWidth) + (segmentWidth / 2);
              
              return (
                <View
                  key={dataPoint.day}
                  style={[
                    styles.xAxisLabelWrapper,
                    {
                      left: centerX - 15, // Subtract half of label width for centering
                    },
                  ]}
                >
                  <Text style={[styles.xAxisLabel, { fontSize: responsiveDims.fontSize }]}>{dataPoint.day}</Text>
                </View>
              );
            })}
          </View>
        </View>

        {/* Responsive Legend */}
        <View style={[
          styles.legendContainer,
          isSmallScreen && styles.legendContainerSmall,
          isMediumScreen && styles.legendContainerMedium,
        ]}>
          {legend.map((item, index) => (
            <View key={index} style={styles.legendItem}>
              <LegendDot color={item.color} />
              <Text style={[styles.legendLabel, { fontSize: responsiveDims.fontSize }]}>{item.label}</Text>
            </View>
          ))}
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
  },
  // Original card surface styling (used for inner container and empty state)
  container: {
    flex: 1,
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: { width: 4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 4,
    position: 'relative',
    maxWidth: '100%',
    height: '100%',
  },
  containerFill: {
    flex: 1,
    width: '100%',
    height: '100%'
  },
  innerContainer: {
    flex: 1,
    backgroundColor: '#FBFCFE',
    borderRadius: 8,
    shadowColor: 'rgba(0, 92, 188, 0.08)',
    shadowOffset: { width: 4, height: 0 },
    shadowOpacity: 1,
    shadowRadius: 24,
    elevation: 4,
    position: 'relative',
    maxWidth: '100%'
  },
  header: {
    position: 'absolute',
    left: 24,
    top: 24,
    right: 24,
  },
  title: {
    color: '#3A3A3A',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Bold',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 16,
    fontWeight: '700',
    lineHeight: 22,
  },
  chartWrapper: {
    position: 'relative',
    flex: 1,
  },
  yAxisContainer: {
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
  },
  yAxisLabelWrapper: {
    position: 'absolute',
    width: 32,
    alignItems: 'flex-end',
    justifyContent: 'center',
    height: 18,
  },
  yAxisLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'right',
  },
  chartArea: {
    position: 'absolute',
    backgroundColor: 'transparent',
  },
  gridLine: {
    position: 'absolute',
    height: 1,
    backgroundColor: '#DFDFDF',
  },
  verticalGridLine: {
    position: 'absolute',
    width: 1,
    backgroundColor: '#DFDFDF',
    top: 0,
  },
  barsContainer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
  },
  barGroup: {
    position: 'absolute',
    bottom: 0,
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 8,
  },
  bar: {
    // Remove flex: 1 since we're setting width explicitly
  },
  xAxisContainer: {
    position: 'absolute',
    height: 22,
  },
  xAxisLabelWrapper: {
    position: 'absolute',
    width: 30,
    alignItems: 'center',
    justifyContent: 'center',
    height: 22,
    // marginLeft: '7%',
    top: 4,
  },
  xAxisLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 12,
    fontWeight: '400',
    lineHeight: 18,
    textAlign: 'center',
  },
  legendContainer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 16,
    height: 20,
  },
  legendContainerSmall: {
    left: '50%',
    marginLeft: -100,
    width: 200,
    bottom: 15,
  },
  legendContainerMedium: {
    left: '50%',
    marginLeft: -110,
    width: 220,
    bottom: 18,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  legendLabel: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 14,
    fontWeight: '400',
    lineHeight: 20,
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 60,
  },
  emptyStateText: {
    color: '#616161',
    fontFamily: Platform.select({
      ios: 'Inter',
      android: 'Inter-Regular',
      web: 'Inter, -apple-system, Roboto, Helvetica, sans-serif',
    }),
    fontSize: 16,
    fontWeight: '400',
    textAlign: 'center',
  },
});

export default DLOBarChart;
